import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  MenuItem,
  Alert,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

import JalaliDateField from "../Components/JalaliDateField";
const CollectPaymentDialog = ({
  open,
  onClose,
  customer,
  token,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem("i18nextLng") ||
    "en"
  ).split("-")[0];
  const isRTL = ["ps", "dr"].includes(currentLanguage);
  const fieldDirection = isRTL ? "rtl" : "ltr";
  const fieldTextAlign = isRTL ? "right" : "left";
  const getTextFieldProps = ({
    inputProps = {},
    InputLabelProps = {},
    sx = {},
  } = {}) => ({
    inputProps: {
      ...inputProps,
      dir: fieldDirection,
      style: {
        ...(inputProps.style || {}),
        textAlign: fieldTextAlign,
      },
    },
    InputLabelProps: {
      ...InputLabelProps,
      sx: {
        direction: fieldDirection,
        right: isRTL ? 24 : "auto",
        left: isRTL ? "auto" : undefined,
        transformOrigin: isRTL ? "top right" : "top left",
        ...(InputLabelProps.sx || {}),
      },
    },
    sx: {
      direction: fieldDirection,
      "& .MuiOutlinedInput-root": {
        direction: fieldDirection,
      },
      "& .MuiInputBase-input": {
        textAlign: fieldTextAlign,
      },
      "& .MuiInputLabel-root": {
        textAlign: fieldTextAlign,
      },
      ...sx,
    },
  });
  const selectProps = {
    MenuProps: {
      PaperProps: {
        sx: { direction: fieldDirection, textAlign: fieldTextAlign },
      },
    },
  };

  const {
    handleAdd: handleBalancePaymentAdd,
    loading: balancePaymentLoading,
    resetForm: balancePaymentReset,
  } = useAdd("shop/customer-balance-payments", token);

  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["collect-payment-accounts", customer?.shop],
    customer?.shop ? `shop/accounts/?page_size=all&shop=${customer.shop}` : "",
    token
  );

  const currencies = getUniqueCurrencies(currenciesData?.results ?? currenciesData ?? []);
  const accounts = Array.isArray(accountsData) ? accountsData : accountsData?.results || [];

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState("");

  const selectedCurrencyId = selectedCurrency?.id ? String(selectedCurrency.id) : "";
  const selectedAccount = useMemo(
    () => accounts.find((a) => String(a.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );

  const { data: directRateData } = useFetchObjects(
    ["collect-payment-rate-direct", customer?.shop, selectedCurrencyId, selectedAccount?.currency],
    customer?.shop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${customer.shop}&from_currency=${selectedCurrencyId}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["collect-payment-rate-reverse", customer?.shop, selectedCurrencyId, selectedAccount?.currency],
    customer?.shop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${customer.shop}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrencyId}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrencyId) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrencyId;
    if (String(selectedCurrencyId) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrencyId, selectedAccount, directRateData, reverseRateData]);

  const convertedAmountPreview = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const resetForm = () => {
    setSelectedCurrency(null);
    setSelectedAccountId("");
    setAmount("");
    setExchangeRate("");
    setPaymentDate(new Date().toISOString().slice(0, 16));
    setNotes("");
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  useEffect(() => {
    if (balancePaymentReset) {
      resetForm();
      onSuccess?.();
      onClose();
    }
  }, [balancePaymentReset, onClose, onSuccess]);

  useEffect(() => {
    if (!selectedCurrencyId) {
      setSelectedAccountId("");
      return;
    }
    if (accounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = accounts.some((a) => String(a.id) === String(selectedAccountId));
    if (!exists) {
      setSelectedAccountId(String(accounts[0].id));
    }
  }, [selectedCurrencyId, accounts, selectedAccountId]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview || "");
  }, [exchangeRatePreview, selectedCurrencyId, selectedAccountId]);

  const handleSubmit = () => {
    if (!selectedCurrency || !selectedAccountId || !amount || !exchangeRate || !customer?.id) return;
    handleBalancePaymentAdd({
      customer: customer.id,
      currency: selectedCurrency.id,
      account: selectedAccountId,
      amount: Number(amount),
      exchange_rate: exchangeRate,
      payment_date: paymentDate,
      notes: notes.trim() || undefined,
    });
  };

  const currentBalance = customer?.total_owed_by_currency;
  const balanceStr =
    currentBalance && typeof currentBalance === "object"
      ? Object.entries(currentBalance)
          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
          .join(", ") || "0"
      : "0";

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>
        {t("customer.collectPayment", "Collect payment")} – {customer.name}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            direction: fieldDirection,
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: "grey.100",
              borderRadius: 1,
              textAlign: fieldTextAlign,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("customer.openingBalanceExistingDues", "Opening balance (existing dues)")}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {balanceStr}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {t(
                "customer.collectPaymentAccountEffect",
                "Collect payment will settle existing dues and add the amount to the selected account."
              )}
            </Typography>
          </Box>

          <TextField
            select
            SelectProps={{ native: true }}
            margin="dense"
            label={t("salePayment.selectCurrency", "Currency")}
            fullWidth
            required
            value={selectedCurrency?.id ?? ""}
            onChange={(e) => {
              const c = currencies.find((x) => String(x.id) === e.target.value);
              setSelectedCurrency(c || null);
            }}
            disabled={currencyLoading}
            {...getTextFieldProps()}
          >
            <option value="" />
            {(currencies || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("salePayment.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedCurrencyId || accountLoading}
            SelectProps={selectProps}
            {...getTextFieldProps()}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              accounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("salePayment.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            {...getTextFieldProps({ inputProps: { min: 0, step: "0.01" } })}
          />
          <TextField
            margin="dense"
            label={t("salePayment.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            {...getTextFieldProps({ inputProps: { min: 0, step: "0.000001" } })}
          />
          <TextField
            margin="dense"
            label={t("salePayment.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmountPreview}
            disabled
            {...getTextFieldProps()}
          />
          {!exchangeRatePreview && selectedCurrencyId && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1, textAlign: fieldTextAlign }}>
              {t(
                "salePayment.missingRate",
                "No exchange rate found for selected account currency. You can enter it manually."
              )}
            </Alert>
          )}
          <JalaliDateField mode="datetime"
            margin="dense"
            label={t("salePayment.paymentDate", "Payment date")}
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            {...getTextFieldProps({ InputLabelProps: { shrink: true } })}
          />
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            {...getTextFieldProps()}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={balancePaymentLoading}>
          {t("customer.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            balancePaymentLoading ||
            !selectedCurrency ||
            !selectedAccountId ||
            !amount ||
            Number(amount) <= 0 ||
            !exchangeRate ||
            Number(exchangeRate) <= 0
          }
          sx={{ minWidth: 100 }}
        >
          {balancePaymentLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("customer.recordPayment", "Record payment")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectPaymentDialog;
